import { Box, TextField, Typography, Autocomplete, CircularProgress, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import axiosService from 'utils/axios';
import { Company, Person } from 'types/entities/types';
import ModalPopup from '../../../components/Modal/ModalPopup';
import AddOrEditPeople from '../people/AddOrEditPeople';

interface Props {
  company?: Company;
  companyName?: string;
  onSave: (company: Company) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface ContactPerson {
  id: string;
  name: string;
}

interface CompanyCreateUpdateError {
  error: string;
}

export default function AddOrEditCompany(props: Props) {
  const { company, companyName = '', onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CompanyCreateUpdateError | null>();

  const [contactPersonOptions, setContactPersonOptions] = useState<ContactPerson[]>([]);
  const [isContactPersonOptionsLoading, setIsContactPersonOptionsLoading] = useState(false);

  const [nameInput, setNameInput] = useState(company?.name || companyName);
  const [emailInput, setEmailInput] = useState(company?.email || '');
  const [showInvalidEmailError, setShowInvalidEmailError] = useState(false);
  const [phoneInput, setPhoneInput] = useState(company?.phone || '');
  const [addressInput, setAddressInput] = useState(company?.address || '');
  const [eoriInput, setEoriInput] = useState(company?.eori || '');
  const [eikBulstatInput, setEikBulstatInput] = useState(company?.eik_bulstat || '');
  const [contactPersonTextInput, setContactPersonTextInput] = useState<string>();
  const [contactPersonChoice, setContactPersonChoice] = useState<ContactPerson | null>(
    company?.contact_person
      ? {
          id: company.contact_person.id,
          name: `${company.contact_person.first_name} ${company.contact_person.last_name}`,
        }
      : null
  );
  const [addNewPersonModalOpen, setAddNewPersonModalOpen] = useState(false);

  const getContactPersonList = useCallback((onSuccess?: () => void) => {
    setIsContactPersonOptionsLoading(true);
    axiosService
      .get(`/entities/person/`)
      .then(res => {
        const names: ContactPerson[] = [];
        res.data.forEach((person: Person) =>
          names.push({ id: person.id, name: `${person.first_name} ${person.last_name}` })
        );
        setContactPersonOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsContactPersonOptionsLoading(false));
  }, []);

  useEffect(() => {
    // Fetch contact person list
    getContactPersonList();
  }, [getContactPersonList]);

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      name: nameInput,
      email: emailInput,
      phone: phoneInput,
      address: addressInput,
      eori: eoriInput,
      // eslint-disable-next-line camelcase
      eik_bulstat: eikBulstatInput,
      // eslint-disable-next-line camelcase
      contact_person_id: contactPersonChoice?.id || '',
    };
    if (company) {
      // Update existing company
      axiosService
        .patch(`/entities/company/${company.id}/`, data)
        .then(companyData => {
          setLoading(false);
          setError(null);
          onSave(companyData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new company
      axiosService
        .post('/entities/company/', data)
        .then(companyData => {
          setLoading(false);
          setError(null);
          onSave(companyData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={!nameInput || showInvalidEmailError}
        >
          {company ? 'Edit company' : 'Create company'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {error ? <Typography>{error.error}</Typography> : null}
          <TextField
            required
            fullWidth
            margin="normal"
            id="name"
            label="Name"
            autoFocus
            value={nameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNameInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="email"
            label="Email"
            type="email"
            value={emailInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const email = event.target.value;
              setEmailInput(email);
              if (
                !!email &&
                // eslint-disable-next-line no-useless-escape
                !/^(mailto:)?([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3\-\_\.\%\+])+\@([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3\-\_\.]+)\.([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3]+)$|^\$email\$$/i.test(
                  email
                )
              ) {
                setShowInvalidEmailError(true);
              } else {
                setShowInvalidEmailError(false);
              }
            }}
            error={showInvalidEmailError}
            helperText={showInvalidEmailError ? 'Please enter a valid email address.' : ''}
          />
          <TextField
            fullWidth
            margin="normal"
            id="phone"
            label="Phone"
            type="tel"
            value={phoneInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPhoneInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="address"
            label="Address"
            value={addressInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddressInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="EIK/Bulstat"
            label="EIK / Bulstat"
            value={eikBulstatInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEikBulstatInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="eori"
            label="EORI"
            value={eoriInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEoriInput(event.target.value);
            }}
          />
          <Autocomplete
            fullWidth
            sx={{ mt: 1 }}
            value={contactPersonChoice}
            onChange={(event, value) => setContactPersonChoice(value)}
            onInputChange={(event, value) => setContactPersonTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={contactPersonOptions}
            loading={isContactPersonOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                label="Contact Person"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewPersonModalOpen(true)}>
                Add a new contact person
              </Button>
            }
          />
          {addNewPersonModalOpen ? (
            <AddOrEditPeople
              personName={contactPersonTextInput}
              onSave={newPerson => {
                getContactPersonList(() => {
                  setContactPersonChoice({
                    id: newPerson.id,
                    name: `${newPerson.first_name} ${newPerson.last_name}`.trim(),
                  });
                  setAddNewPersonModalOpen(false);
                });
              }}
              modalTitle="Add new person"
              isModalOpen={addNewPersonModalOpen}
              setModalOpen={setAddNewPersonModalOpen}
            />
          ) : null}
        </Box>
      </Box>
    </ModalPopup>
  );
}
